import { useState, useRef, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';

/**
 * @param {(...args)=> void} value
 * @param {number} waitMs
 * @param {import('lodash').DebounceSettingsLeading} [options]
 * @returns {[any, (newVal: any) => void]}
 */
export function useDebounceCallback(callback, waitMs, options) {
  const callbackRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debounceCallback = useMemo(
    () =>
      debounce(
        (...args) => {
          const currentCallback = callbackRef.current;
          if (currentCallback) {
            currentCallback(...args);
          }
        },
        waitMs,
        options,
      ),
    [],
  );

  useEffect(() => {
    // cancel when unmounted
    return () => {
      debounceCallback.cancel();
    };
  }, []);

  return debounceCallback;
}

/**
 * @param {any} value
 * @param {number} waitMs
 * @param {import('lodash').DebounceSettingsLeading} [options]
 * @returns {[any, (newVal: any) => void]}
 */
export default function useDebounce(value, waitMs, options) {
  const [state, setState] = useState(null);
  const setStateRef = useRef(null);

  const debounceSetState = useDebounceCallback(
    newState => {
      const setStateCallback = setStateRef.current;
      if (setStateCallback) {
        setStateCallback(newState);
      }
    },
    waitMs,
    options,
  );

  useEffect(() => {
    setStateRef.current = setState;
  }, [setState]);

  useEffect(() => {
    debounceSetState(value);
  }, [value]);

  return [state, debounceSetState];
}
